import { useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, Upload, Users, PhoneCall, ThumbsUp, ThumbsDown, Footprints } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { EmptyState } from "@/components/app/Loader";
import { StatCard } from "@/components/app/StatCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const RESULT_COLOR = {
  "Strong support": "#1E7E34", "Lean support": "#5CB85C", "Undecided": "#B8860B",
  "Lean oppose": "#E08E45", "Strong oppose": "#C0392B", "Not home": "#94A3B8",
  "Refused": "#64748B", "Moved": "#94A3B8", "Bad number": "#94A3B8", "Contacted": "#0077B6",
};

export default function Canvassing() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const { data } = useQuery({ queryKey: ["canvass"], queryFn: () => api.get("/voters/canvass-results/summary").then((r) => r.data) });

  const doImport = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/voters/canvass-results/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Recorded ${res.recorded} results · skipped ${res.skipped}`);
      qc.invalidateQueries({ queryKey: ["canvass"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const maxResult = Math.max(1, ...Object.values(data?.by_result || { x: 1 }));

  return (
    <div>
      <PageHeader
        title="Canvassing"
        subtitle="Close the loop on the field: re-upload completed walk/call sheets to build a contact history that will power GOTV."
        icon={ClipboardCheck}
        actions={
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => doImport(e.target.files?.[0])} />
            <Button asChild variant="outline"><Link to="/segments"><Footprints className="mr-2 h-4 w-4" /> Get a walk list</Link></Button>
            <Button onClick={() => fileRef.current?.click()} className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="upload-canvass-btn">
              <Upload className="mr-2 h-4 w-4" /> Upload results
            </Button>
          </div>
        }
      />

      <div className="mb-6 rounded-lg border border-brand-blue/20 bg-brand-blue/[0.04] px-4 py-3 text-sm text-brand-navy">
        <strong>How it works:</strong> Export a Walk or Call list from <Link to="/segments" className="font-semibold text-brand-blue hover:underline">Segments</Link>, fill the <em>Result</em> column
        (Strong support, Lean support, Undecided, Lean oppose, Strong oppose, Not home, Refused…), then upload the file here. Rows are matched on Voter ID.
      </div>

      {!data?.total ? (
        <EmptyState icon={ClipboardCheck} title="No canvass results yet" description="Upload a completed walk or call sheet to start tracking field contacts."
          action={<Button onClick={() => fileRef.current?.click()} className="bg-brand-red text-white"><Upload className="mr-2 h-4 w-4" /> Upload results</Button>} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard testId="canvass-total" icon={Users} accent="navy" label="Records" value={data.total} hint="voters touched" />
            <StatCard testId="canvass-contacted" icon={PhoneCall} accent="blue" label="Contacted" value={data.contacted} hint="reached at door/phone" />
            <StatCard testId="canvass-support" icon={ThumbsUp} accent="green" label="Supportive" value={data.supportive} hint="strong + lean support" />
            <StatCard testId="canvass-oppose" icon={ThumbsDown} accent="red" label="Opposed" value={data.opposed} hint="strong + lean oppose" />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg font-bold text-brand-ink">Result breakdown</h3>
              <div className="mt-4 space-y-3">
                {Object.entries(data.by_result).map(([k, v]) => (
                  <div key={k}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="font-medium text-brand-ink">{k}</span>
                      <span className="text-brand-mute">{v}</span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-slate-100">
                      <div className="h-2.5 rounded-full transition-all" style={{ width: `${(v / maxResult) * 100}%`, background: RESULT_COLOR[k] || "#1D3557" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg font-bold text-brand-ink">By precinct</h3>
              <div className="mt-3 max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Precinct</TableHead><TableHead className="text-right">Contacts</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.by_precinct.map((p) => (
                      <TableRow key={p.precinct || "-"}>
                        <TableCell className="font-medium text-brand-ink">{p.precinct || "-"}</TableCell>
                        <TableCell className="text-right">{p.n}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          <div className="mt-6 overflow-hidden rounded-xl border border-border bg-white shadow-sm">
            <div className="border-b border-border px-5 py-3"><h3 className="font-heading text-sm font-bold text-brand-ink">Recent entries</h3></div>
            <Table>
              <TableHeader><TableRow><TableHead>Voter ID</TableHead><TableHead>Precinct</TableHead><TableHead>Result</TableHead><TableHead>By</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.recent.map((r) => (
                  <TableRow key={r.id || r.voterId}>
                    <TableCell className="font-mono text-xs text-brand-ink">{r.voterId}</TableCell>
                    <TableCell>{r.precinct || "-"}</TableCell>
                    <TableCell><Badge variant="outline" style={{ color: RESULT_COLOR[r.result] || "#1D3557", borderColor: `${RESULT_COLOR[r.result] || "#1D3557"}40` }}>{r.result}</Badge></TableCell>
                    <TableCell className="text-xs text-brand-mute">{r.recorded_by}</TableCell>
                    <TableCell className="text-xs text-brand-mute">{timeAgo(r.recorded_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
